import axios from 'axios';
import { API_BASE_URL } from './api';

const API_URL = API_BASE_URL;

// PI Plan interfaces
export interface PIPlan {
  _id: string;
  name: string;
  fiscalYear: string;
  quarter: number;
  piNumber: number;
  startDate: string;
  endDate: string;
  status: 'Planning' | 'In Progress' | 'Completed' | 'Cancelled';
  objectives: PIObjective[];
  teams: Team[];
  sprints: Sprint[];
  risks: Risk[];
  dependencies: Dependency[];
  metrics: Metrics;
  createdAt: string;
  updatedAt: string;
}

export interface PIObjective {
  objectiveId: string;
  description: string;
  businessValue: number;
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  assignedTeam: string;
  status: 'Not Started' | 'In Progress' | 'At Risk' | 'Completed' | 'Blocked';
}

export interface Team {
  teamId: string;
  teamName: string;
  capacity: number;
  velocity: number;
  members: number;
}

export interface Sprint {
  sprintNumber: number;
  startDate: string;
  endDate: string;
  goals: string[];
  status: 'Not Started' | 'In Progress' | 'Completed';
}

export interface Risk {
  _id?: string;
  riskId: string;
  description: string;
  roamCategory: 'Resolved' | 'Owned' | 'Accepted' | 'Mitigated';
  severity: 'Critical' | 'High' | 'Medium' | 'Low';
  owner: string;
  mitigationPlan: string;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  createdDate: string;
  resolvedDate?: string;
}

export interface Dependency {
  dependencyId: string;
  fromTeam: string;
  toTeam: string;
  description: string;
  status: 'Identified' | 'Planned' | 'Resolved' | 'Blocked';
}

export interface Metrics {
  plannedVelocity: number;
  actualVelocity: number;
  commitmentReliability: number;
  predictabilityMeasure: number;
}

export interface Epic {
  _id: string;
  epicId: string;
  title: string;
  description: string;
  epicType: 'Business' | 'Enabler' | 'Architectural';
  piPlan?: string;
  fiscalYear: string;
  quarter: number;
  owner: string;
  status: 'Backlog' | 'Analyzing' | 'Portfolio Review' | 'Implementing' | 'Done' | 'Cancelled';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  businessValue: number;
  timeCriticality: number;
  riskReduction: number;
  wsjfScore: number;
  estimatedEffort: 'XS' | 'S' | 'M' | 'L' | 'XL' | 'XXL';
  acceptanceCriteria: string[];
  assignedTeams: string[];
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RelatedLink {
  _id?: string;
  label: string;
  url: string;
  description?: string;
  linkType?: 'documentation' | 'milestone' | 'epic' | 'external' | 'dashboard' | 'report' | 'other';
}

export interface Story {
  _id: string;
  storyId: string;
  title: string;
  description: string;
  epic?: Epic;
  epicId: string;
  piPlan?: string;
  sprint: {
    sprintNumber: number;
    startDate: string;
    endDate: string;
  };
  assignedTeam: string;
  assignedTo?: string;
  status: 'Backlog' | 'Ready' | 'In Progress' | 'In Review' | 'Testing' | 'Done' | 'Blocked';
  priority: 'Critical' | 'High' | 'Medium' | 'Low';
  storyPoints: number;
  acceptanceCriteria: string[];
  tags: string[];
  relatedLinks?: RelatedLink[];
  createdAt: string;
  updatedAt: string;
}

// PI Planning API calls
export const getAllPIPlans = async (): Promise<PIPlan[]> => {
  const response = await axios.get(`${API_URL}/pi-planning`, { withCredentials: true });
  return response.data.data;
};

export const getPIPlanById = async (id: string): Promise<PIPlan> => {
  const response = await axios.get(`${API_URL}/pi-planning/${id}`, { withCredentials: true });
  return response.data.data;
};

export const createPIPlan = async (piPlan: Partial<PIPlan>): Promise<PIPlan> => {
  const response = await axios.post(`${API_URL}/pi-planning`, piPlan, { withCredentials: true });
  return response.data.data;
};

export const updatePIPlan = async (id: string, piPlan: Partial<PIPlan>): Promise<PIPlan> => {
  const response = await axios.put(`${API_URL}/pi-planning/${id}`, piPlan, { withCredentials: true });
  return response.data.data;
};

export const deletePIPlan = async (id: string): Promise<void> => {
  await axios.delete(`${API_URL}/pi-planning/${id}`, { withCredentials: true });
};

export const getPIPlanEpics = async (piPlanId: string): Promise<Epic[]> => {
  const response = await axios.get(`${API_URL}/pi-planning/${piPlanId}/epics`, { withCredentials: true });
  return response.data.data;
};

export const getPIPlanStories = async (piPlanId: string): Promise<Story[]> => {
  const response = await axios.get(`${API_URL}/pi-planning/${piPlanId}/stories`, { withCredentials: true });
  return response.data.data;
};

export const addRiskToPIPlan = async (piPlanId: string, risk: Partial<Risk>): Promise<PIPlan> => {
  const response = await axios.post(`${API_URL}/pi-planning/${piPlanId}/risks`, risk, { withCredentials: true });
  return response.data.data;
};

export const updateRiskInPIPlan = async (piPlanId: string, riskId: string, risk: Partial<Risk>): Promise<PIPlan> => {
  const response = await axios.put(`${API_URL}/pi-planning/${piPlanId}/risks/${riskId}`, risk, { withCredentials: true });
  return response.data.data;
};

// Epics API calls
export const getAllEpics = async (filters?: any): Promise<Epic[]> => {
  const response = await axios.get(`${API_URL}/epics`, { 
    params: filters,
    withCredentials: true 
  });
  return response.data.data;
};

export const getEpicById = async (id: string): Promise<Epic> => {
  const response = await axios.get(`${API_URL}/epics/${id}`, { withCredentials: true });
  return response.data.data;
};

export const createEpic = async (epic: Partial<Epic>): Promise<Epic> => {
  const response = await axios.post(`${API_URL}/epics`, epic, { withCredentials: true });
  return response.data.data;
};

export const updateEpic = async (id: string, epic: Partial<Epic>): Promise<Epic> => {
  const response = await axios.put(`${API_URL}/epics/${id}`, epic, { withCredentials: true });
  return response.data.data;
};

export const deleteEpic = async (id: string): Promise<void> => {
  await axios.delete(`${API_URL}/epics/${id}`, { withCredentials: true });
};

// Stories API calls
export const getAllStories = async (filters?: any): Promise<Story[]> => {
  const response = await axios.get(`${API_URL}/stories`, { 
    params: filters,
    withCredentials: true 
  });
  return response.data.data;
};

export const getStoryById = async (id: string): Promise<Story> => {
  const response = await axios.get(`${API_URL}/stories/${id}`, { withCredentials: true });
  return response.data.data;
};

export const createStory = async (story: Partial<Story>): Promise<Story> => {
  const response = await axios.post(`${API_URL}/stories`, story, { withCredentials: true });
  return response.data.data;
};

export const updateStory = async (id: string, story: Partial<Story>): Promise<Story> => {
  const response = await axios.put(`${API_URL}/stories/${id}`, story, { withCredentials: true });
  return response.data.data;
};

export const deleteStory = async (id: string): Promise<void> => {
  await axios.delete(`${API_URL}/stories/${id}`, { withCredentials: true });
};
